<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { real_id } from '../../../helpers/interface_helpers';
	//Services
	import { postReq } from '../../../services/http';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	// Design system components
	import { Text, Button, Container, Modal } from '@Shelly/shelly-design-system';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { sortBy, sortByPosition } from 'src/components/Lists/list-helpers';

	import type { Device } from 'src/types/Device';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	let showModal = false;
	let loading = false;

	let allItemsRefs: { [key: string | number]: HTMLElement } = {};

	let selectedDevices = Object.values($devices)
		.filter((d) => d?.cloud_options?.measure_peak_power)
		.map((d) => d.id);

	const MAX_PEAK_POWER_DEVICES = 5;

	const toastTexts = {
		success: $format('toast_succ_measure_peak_power', {
			default: 'Device enabled for "Peak Power" measurements.',
		}),
		danger: $format('setting_err_toast', {
			default: 'Failed to apply settings.',
		}),
	};

	const savePeakPower = async () => {
		loading = true;
		let res;

		for (let index = 0; index < selectedDevices.length; index++) {
			const id = selectedDevices[index];
			const _device = $devices[id];

			res = await postReq('interface/device/set_cloud_options', {
				id: id,
				set: 'measure_peak_power',
				val: true,
			});
			if (res?.isok) {
				_device.cloud_options.measure_peak_power = true;
				devices.update(_device);
			} else break;
		}

		onSettingsResponse(res, null, toastTexts);
		loading = false;
		if (res?.isok) {
			showModal = false;
		}
	};

	const handleSelection = (item) => {
		if (selectedDevices.includes(item.id)) {
			selectedDevices.splice(selectedDevices.indexOf(item.id), 1);
		} else {
			selectedDevices.push(item.id);
		}

		roomsFiltered = sortListByPosition(filterDevices(Object.values($devices)));
	};

	const sortListByPosition = (list: any) => {
		return list.sort((x: any, y: any) => {
			return sortBy(x, y, 'position');
		});
	};

	const filterDevices: any = (array: Device[]) => {
		let filteredDevicesByRoom = {};

		const filteredArray = array.filter(
			(el) =>
				el.category === 'relay' ||
				el.category === 'light' ||
				el.category === 'roller' ||
				el.category === 'emeter',
		);

		filteredArray.forEach((device: Device, i) => {
			const roomID = device.room_id;

			const deviceData = {
				name: `${device.name}${
					!statusSnapshot?.[real_id(device.id)]?.online
						? `- ${capFirstChar($format('label_offline'))}`
						: ''
				}`,
				id: device.id,
				selected: selectedDevices.findIndex((el) => el === device.id) > -1,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image ? device.image : undefined,
				backgroundColor: device.backgroundColor ? device.backgroundColor : undefined,
				icon: device.icon ? device.icon : undefined,
				disabled:
					selectedDevices.findIndex((el) => el === device.id) == -1 &&
					selectedDevices.length >= MAX_PEAK_POWER_DEVICES,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID].cards = [
					...filteredDevicesByRoom[roomID].cards,
					deviceData,
				];
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
				};
			}
		});

		return Object.values(filteredDevicesByRoom);
	};

	$: statusSnapshot = $devices_status;
	$: roomsFiltered = (statusSnapshot, sortListByPosition(filterDevices(Object.values($devices))));
</script>

<div class="mb-s">
	<Text element="h2" class="mb-s">
		{$format('title_peak_power_details', {
			default: 'Peak Power measurement',
		})}
	</Text>

	<Text class="mb-s">Choose which devices to measure peak power consumption.</Text>

	<Button
		size="m"
		class="text-capitalize width-md-auto width-sm-full"
		on:click={() => (showModal = true)}
		>{$format('button_choose_devices', { default: 'choose devices' })}</Button
	>
</div>

{#if showModal}
	<Modal
		{showModal}
		on:close={() => (showModal = false)}
		title={$format('modal_title_peak_power', {
			default: 'Peak Power measurement devices',
		})}
	>
		<Container slot="content" padding="0">
			<!-- DO NOT REMOVE COMMENT: Padding references -->
			<div class="pt-s"></div>
			<SelectList
				title={$format('label_selected', {
					default: 'Selected:',
				})}
				titleHighlight={selectedDevices.length}
				list={roomsFiltered}
				bind:allCardsRefs={allItemsRefs}
				on:click={(e) => {
					handleSelection(e.detail);
				}}
			/>
		</Container>

		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				{loading}
				variant="secondary"
				on:click={() => (showModal = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="primary"
				responsiveSize={{
					sm: 'm',
				}}
				{loading}
				disabled={selectedDevices.length > MAX_PEAK_POWER_DEVICES}
				on:click={savePeakPower}>{$format('button_save', { default: 'Save' })}</Button
			>
		</div>
	</Modal>
{/if}
